import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, PauseCircle, Clock, CreditCard, ExternalLink, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
// 🔒 PAYPAL-FASE4-2026-05 — page de pago ahora vive en este mismo pod.
const PAY_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/pay`;
const SUPPORT_EMAIL = 'soporte@vexlyapp.com';
const POLL_INTERVAL_MS = 60_000; // 1 min

const daysUntil = (iso) => {
  if (!iso) return null;
  const target = new Date(iso + 'T23:59:59').getTime();
  return Math.ceil((target - Date.now()) / 86400000);
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};

/**
 * Globally renders:
 *   - Banner at top of <children> if grace_period or expiring-in-7 days.
 *   - Full-screen block if suspended AND user is not admin/super_admin.
 *   - Nothing for active subscriptions with >7 days remaining.
 *
 * Polls /api/subscriptions/me every minute to stay in sync with backend
 * state (e.g. admin suspends from super-admin panel).
 */
export default function SubscriptionGuard({ children }) {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);

  const fetchMe = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('pos_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/api/subscriptions/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSub(res.data);
    } catch {
      // 404: pod not registered yet → silently ignore
      // 403: would be blocked by guard itself; leave null
    }
  }, [user]);

  useEffect(() => {
    fetchMe();
    const t = setInterval(fetchMe, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchMe]);

  if (!user || !sub) return children;

  const status = sub.status || 'active';
  const d = daysUntil(sub.current_period_end);
  const graceD = daysUntil(sub.grace_period_end);
  const payUrl = `${PAY_URL}?client=${encodeURIComponent(sub.client_id)}`;

  // FULL BLOCK — non-admin user on a suspended pod
  if (status === 'suspended' && !isAdmin) {
    return <SuspendedFullScreen payUrl={payUrl} />;
  }

  // Admin on suspended pod → allow but show persistent top banner
  if (status === 'suspended' && isAdmin) {
    return (
      <>
        <SuspendedAdminBanner payUrl={payUrl} onGoToSub={() => navigate('/settings?tab=subscription')} />
        {children}
      </>
    );
  }

  if (status === 'grace_period') {
    return (
      <>
        <GraceBanner graceDays={graceD} graceEnd={sub.grace_period_end} payUrl={payUrl} onGoToSub={() => navigate('/settings?tab=subscription')} />
        {children}
      </>
    );
  }

  if (status === 'active' && d != null && d <= 7 && d >= 0) {
    return (
      <>
        <ExpiringSoonBanner days={d} end={sub.current_period_end} payUrl={payUrl} onGoToSub={() => navigate('/settings?tab=subscription')} />
        {children}
      </>
    );
  }

  return children;
}

// -------------------- Components --------------------

function ExpiringSoonBanner({ days, end, payUrl, onGoToSub }) {
  return (
    <div className="sticky top-0 z-[60] bg-orange-500/95 backdrop-blur text-white px-4 py-2 text-sm flex flex-wrap items-center gap-3 shadow" data-testid="global-banner-expiring">
      <Clock size={16} className="shrink-0" />
      <span className="flex-1 min-w-0">
        Tu suscripción vence en <b>{days} día{days === 1 ? '' : 's'}</b> ({formatDate(end)}). Renueva para evitar interrupciones.
      </span>
      <a href={payUrl} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 font-semibold text-xs transition-all"
         data-testid="global-banner-expiring-pay">
        <CreditCard size={12} /> Renovar
      </a>
      <button onClick={onGoToSub}
         className="underline text-xs opacity-90 hover:opacity-100"
         data-testid="global-banner-expiring-details">
        Detalles
      </button>
    </div>
  );
}

function GraceBanner({ graceDays, graceEnd, payUrl, onGoToSub }) {
  return (
    <div className="sticky top-0 z-[60] bg-amber-600/95 backdrop-blur text-white px-4 py-2.5 text-sm flex flex-wrap items-center gap-3 shadow" data-testid="global-banner-grace">
      <AlertTriangle size={16} className="shrink-0" />
      <span className="flex-1 min-w-0">
        Tu suscripción venció. {graceDays != null ? <>Quedan <b>{graceDays} día{graceDays === 1 ? '' : 's'}</b> de gracia (hasta {formatDate(graceEnd)}) antes de suspender.</> : 'Período de gracia activo.'}
      </span>
      <a href={payUrl} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white text-amber-700 font-bold text-xs transition-all hover:bg-white/90"
         data-testid="global-banner-grace-pay">
        <CreditCard size={12} /> Pagar ahora
      </a>
      <button onClick={onGoToSub} className="underline text-xs opacity-90 hover:opacity-100" data-testid="global-banner-grace-details">
        Detalles
      </button>
    </div>
  );
}

function SuspendedAdminBanner({ payUrl, onGoToSub }) {
  return (
    <div className="sticky top-0 z-[60] bg-red-700 text-white px-4 py-3 text-sm flex flex-wrap items-center gap-3 shadow-lg" data-testid="global-banner-suspended-admin">
      <PauseCircle size={18} className="shrink-0" />
      <span className="flex-1 min-w-0">
        <b>Sistema suspendido.</b> Los usuarios no pueden iniciar sesión. Los datos están intactos.
      </span>
      {/* 🔒 SUBSCRIPTION-MGMT-2026-06-01 — Oculto hasta integración PayPal
      <a href={payUrl} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white text-red-700 font-bold text-xs transition-all hover:bg-white/90"
         data-testid="global-banner-suspended-pay">
        <CreditCard size={12} /> Pagar y reactivar
      </a>
      */}
      <button onClick={onGoToSub} className="underline text-xs opacity-90 hover:opacity-100" data-testid="global-banner-suspended-details">
        Detalles
      </button>
    </div>
  );
}

function SuspendedFullScreen({ payUrl }) {
  return (
    <div className="fixed inset-0 z-[10000] bg-background text-foreground flex items-center justify-center p-4" data-testid="suspended-full-screen">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-5">
          <PauseCircle className="text-red-600 dark:text-red-400" size={32} />
        </div>
        <h1 className="font-oswald text-2xl font-bold mb-2">Sistema temporalmente inactivo</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Este sistema VexlyPOS está suspendido. Contacte al administrador del establecimiento para más información.
        </p>
        <div className="flex flex-col gap-2">
          {/* 🔒 SUBSCRIPTION-MGMT-2026-06-01 — Botón "Ver opciones de pago"
              oculto temporalmente hasta completar integración PayPal.
          <a href={payUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-sm transition-all hover:bg-primary/90"
             data-testid="suspended-fullscreen-pay">
            <CreditCard size={16} /> Ver opciones de pago
            <ExternalLink size={12} />
          </a>
          */}
          <a href={`mailto:${SUPPORT_EMAIL}`}
             className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border font-semibold text-sm text-muted-foreground hover:bg-muted transition-all"
             data-testid="suspended-fullscreen-support">
            <Mail size={14} /> Contactar soporte
          </a>
        </div>
        <p className="text-[10px] text-muted-foreground mt-6">
          VexlyPOS · Los datos de tu negocio están seguros y se restaurarán al renovar.
        </p>
      </div>
    </div>
  );
}
